"use client";

import { ArrowLeftRight, BriefcaseBusiness, Check, ChevronDown, LogOut, Plus, Settings, ShieldCheck, User } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Avatar } from "@/components/Avatar";
import {
  getSavedAccounts,
  saveAccount,
  removeSavedAccount,
  type SavedAccount,
} from "@/lib/saved-accounts";
import { signOutAction, switchAccountAction } from "@/lib/auth-actions";

function seedFromString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

export function UserMenu({
  userName,
  userRole,
  userSeed,
  userPhotoUrl,
  userEmail,
  canOpenAdminSettings,
}: {
  userName: string;
  userRole: string | null;
  userSeed: number;
  userPhotoUrl?: string | null;
  userEmail?: string | null;
  canOpenAdminSettings: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const email = userEmail ?? null;
  const [savedAccounts, setSavedAccounts] = useState<SavedAccount[]>([]);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (email) saveAccount({ name: userName, email, photoUrl: userPhotoUrl ?? null });
  }, [email, userName, userPhotoUrl]);

  useEffect(() => {
    if (!menuOpen) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenuOpen(false);
      }
    }

    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onClickOutside);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onClickOutside);
    };
  }, [menuOpen]);

  function toggleMenu() {
    if (!menuOpen) setSavedAccounts(getSavedAccounts());
    setMenuOpen(!menuOpen);
  }

  function handleRemoveAccount(targetEmail: string) {
    const updated = removeSavedAccount(targetEmail);
    setSavedAccounts(updated);
  }

  const otherAccounts = savedAccounts.filter(
    (acc) => !email || acc.email.toLowerCase() !== email.toLowerCase(),
  );

  return (
    <div className="relative ml-2" ref={menuRef}>
      <button
        type="button"
        className={`flex h-11 items-center gap-2 rounded-full py-1 pl-1 pr-2.5 transition-colors hover:bg-surface-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand ${menuOpen ? "bg-surface-muted" : ""}`}
        onClick={toggleMenu}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        aria-label="User menu"
      >
        <Avatar name={userName} seed={userSeed} photoUrl={userPhotoUrl} size={28} />
        <span className="hidden min-w-0 max-w-[10rem] flex-col items-start leading-tight sm:flex">
          <span className="max-w-full truncate whitespace-nowrap text-sm font-medium text-fg" title={userName}>
            {userName.length > 16 ? userName.split(" ")[0] : userName}
          </span>
          {userRole && (
            <span className="max-w-full truncate whitespace-nowrap text-xs text-muted" title={userRole}>
              {userRole}
            </span>
          )}
        </span>
        <ChevronDown
          size={16}
          className={`hidden text-subtle transition-transform duration-150 sm:block ${menuOpen ? "rotate-180" : ""}`}
        />
      </button>

      {menuOpen && (
        <div
          role="menu"
          aria-label="User menu"
          className="absolute right-0 top-full z-overlay mt-2 w-[340px] max-w-[calc(100vw-1rem)] overflow-hidden rounded-2xl border border-line bg-surface p-2 shadow-xl"
        >
          {/* Who is signed in: light card, role as a pill (owner, 2026-11-02: no black band, no "View my profile"). */}
          <div className="flex items-center gap-3 rounded-xl bg-gradient-to-br from-brand/10 via-accent/10 to-success/10 p-3">
            <div className="rounded-full ring-2 ring-surface">
              <Avatar name={userName} seed={userSeed} photoUrl={userPhotoUrl} size={44} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-fg" title={userName}>{userName}</div>
              {email && <div className="truncate text-xs text-muted" title={email}>{email}</div>}
              {userRole && (
                <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 text-[11px] font-medium text-fg-soft ring-1 ring-inset ring-line">
                  <BriefcaseBusiness size={11} aria-hidden />
                  {userRole}
                </span>
              )}
            </div>
          </div>

          <div className="mt-1 space-y-0.5 py-1">
            <MenuLink href="/profile" icon={User} onClick={() => setMenuOpen(false)}>My profile</MenuLink>
            <MenuLink href="/settings" icon={Settings} onClick={() => setMenuOpen(false)}>Settings</MenuLink>
            {canOpenAdminSettings && <MenuLink href="/admin/settings" icon={ShieldCheck} onClick={() => setMenuOpen(false)}>Admin settings</MenuLink>}
          </div>

          <div className="border-t border-line pt-1">
            {otherAccounts.length > 0 ? (
              <>
                <div className="px-3 pb-1 pt-2 text-xs font-semibold text-muted">Accounts</div>
                <div className="space-y-0.5">
                  {email && (
                    <AccountRow name={userName} email={email} photoUrl={userPhotoUrl ?? null} seed={userSeed} current>
                      {/* No onClick that closes the menu: unmounting the form in the same click cancels the submission. */}
                      <form action={signOutAction}>
                        <IconAction label="Sign out" type="submit"><LogOut size={15} aria-hidden /></IconAction>
                      </form>
                    </AccountRow>
                  )}
                  {otherAccounts.map((account) => (
                    <AccountRow key={account.email} name={account.name} email={account.email} photoUrl={account.photoUrl ?? null} seed={seedFromString(account.email)}>
                      <form action={switchAccountAction}>
                        <input type="hidden" name="loginHint" value={account.email} />
                        <IconAction label={`Switch to ${account.name}`} type="submit"><ArrowLeftRight size={15} aria-hidden /></IconAction>
                      </form>
                      <IconAction label={`Sign out ${account.name} on this device`} onClick={() => handleRemoveAccount(account.email)}>
                        <LogOut size={15} aria-hidden />
                      </IconAction>
                    </AccountRow>
                  ))}
                </div>
                <AddAccount />
              </>
            ) : (
              <>
                <AddAccount />
                <form action={signOutAction}>
                  <button type="submit" className={ROW}>
                    <LogOut size={16} className="text-muted" aria-hidden />
                    <span>Sign out</span>
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const ROW = "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-fg-soft transition-colors hover:bg-surface-muted hover:text-fg";

function MenuLink({ href, icon: Icon, onClick, children }: { href: string; icon: typeof User; onClick: () => void; children: ReactNode }) {
  return (
    <Link href={href} onClick={onClick} className={ROW}>
      <Icon size={16} className="text-muted" aria-hidden />
      <span>{children}</span>
    </Link>
  );
}

function IconAction({ label, type = "button", onClick, children }: { label: string; type?: "button" | "submit"; onClick?: () => void; children: ReactNode }) {
  return (
    <button
      type={type}
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-8 w-8 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-strong hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
    >
      {children}
    </button>
  );
}

function AccountRow({ name, email, photoUrl, seed, current, children }: { name: string; email: string; photoUrl: string | null; seed: number; current?: boolean; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-surface-muted">
      <Avatar name={name} seed={seed} photoUrl={photoUrl} size={30} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 truncate text-sm text-fg">
          <span className="truncate">{name}</span>
          {current && <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-success-subtle px-1.5 text-[10px] font-semibold text-success-strong"><Check size={10} aria-hidden />Current</span>}
        </div>
        <div className="truncate text-xs text-muted">{email}</div>
      </div>
      <div className="flex shrink-0 items-center gap-0.5">{children}</div>
    </div>
  );
}

function AddAccount() {
  return (
    <form action={switchAccountAction}>
      <button type="submit" className={ROW}>
        <Plus size={16} className="text-muted" aria-hidden />
        <span>Add another account</span>
      </button>
    </form>
  );
}
